/// <amd-module name="Build/Queue-Panel/VariablesControllerView" />
import * as CommonResources from "Build/Common/Library/Resources";
import * as Resources from "Build/Queue-Panel/Resources";
import { QueueBuildStore } from "Build/Queue-Panel/Stores/QueueBuild";
import { IVariablePanelProps, VariablePanel } from "Build/Queue-Panel/VariablePanel";
import * as React from "react";
import { IVariableData } from "Types";
import { IObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { Button, IButtonProps } from "VSSUI/Button";
import { Icon, IconSize } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IListItemDetails, IListRow, ListItem, ListSelection, ScrollableList } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { getSafeId } from "VSSUI/Util";

const c_variablesHelpLink = "https://go.microsoft.com/fwlink/?linkid=2087183";
const c_enforcedVariablesHelpLink = "https://aka.ms/AzPipelines/SettingVariablesAtQueueTime";

export interface IVariablesControllerViewProps {
    onDismiss: () => void;
    pageContext: IVssPageContext;
    pipelineName: string;
    store: QueueBuildStore;
    enforceSettableVar: boolean;
    allowEditing: boolean;
}

export class VariablesControllerView extends React.Component<IVariablesControllerViewProps> {
    private _listSelection: ListSelection = new ListSelection();
    private _itemProvider: IObservableArray<any>;

    private c_hiddenAriaDivId = "hiddenAriaDiv";
    constructor(props: IVariablesControllerViewProps) {
        super(props);
        this._itemProvider = this.props.store.runtimeVariables;
    }

    public render(): JSX.Element {
        return (
            <Panel
                backButtonProps={{ onClick: this.props.onDismiss }}
                contentClassName="queue-panel-child no-padding"
                description={this.props.pipelineName}
                escDismiss={true}
                footerButtonProps={this.getFooterButtonProps()}
                onDismiss={this.props.onDismiss}
                titleProps={{ text: Resources.VariablesTitle }}
            >
                <div className="queue-panel-list-container">
                    <div className="flex-row scroll-auto" onKeyDown={this._onKeyDown}>
                        <ScrollableList
                            ariaLabel={Resources.VariablesTitle}
                            itemProvider={this._itemProvider}
                            renderRow={this._renderRow}
                            width="100%"
                            singleClickActivation={true}
                            selection={this._listSelection}
                            onActivate={this.props.allowEditing ? this._updateVariable: undefined}
                            outerClassName="custom-scrollbar"
                        />
                    </div>
                    <Observer variables={this._itemProvider}>
                        {(props: { variables: IVariableData[] }) =>
                            props.variables.length === 0 ? (
                                <span className="flex-row margin-vertical-8 padding-horizontal-20 primary-text">
                                    {this._renderEmptyVariableMessage()}
                                </span>
                            ) : null
                        }
                    </Observer>
                </div>
            </Panel>
        );
    }

    private _onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        // If delete key is pressed with a variable selected, delete it
        const key = event.key.toLowerCase();
        if (key === "delete" && this._listSelection.selectedCount > 0) {
            const index = this._listSelection.value[0].beginIndex;
            const item = this._itemProvider.value[index];
            if (!item.disableDelete) {
                this.props.store.deleteVariable(index);
                event.preventDefault();
            }
        }
    };

    private _renderEmptyVariableMessage() {
        if (this.props.enforceSettableVar) {
            return (
                <>
                    <label className="margin-right-4">{Resources.EnforcedEmptyVariablesDescription}</label>
                    <Link href={c_enforcedVariablesHelpLink} ariaLabel={Resources.VariablesLearnMore}>
                        {Resources.LearnMoreMessage}
                    </Link>
                </>
            )
        }

        return ( 
            <>
                <label className="margin-right-4">{Resources.EmptyVariablesDescription}</label>
                <Link href={c_variablesHelpLink} ariaLabel={Resources.VariablesLearnMore}>
                    {Resources.LearnMoreMessage}
                </Link>
            </>
        )
    }

    private _renderRow = (index: number, item: IVariableData, details: IListItemDetails<IVariableData>): JSX.Element => {
        const message = item.name ? format(Resources.DeleteButtonTooltip, item.name) : Resources.DeleteButtonEmptyTooltip;
        const value = item.isSecret ? CommonResources.VariableSecretMask : "= " + item.value;
        const iconType = item.isSecret ? "Lock" : "Variable";
        const hiddenAriaDivSafeId = getSafeId(this.c_hiddenAriaDivId);

        return (
            <ListItem key={"list-item" + index} index={index} details={details}>
                <div className="queue-panel-list-row flex-grow flex-row">
                    <Icon iconName={iconType} size={IconSize.medium} className="queue-panel-list-icon margin-right-8" />
                    <div className="flex-column justify-center margin-left-4" aria-describedby={hiddenAriaDivSafeId}>
                        <span className="text-ellipsis primary-text" aria-label={item.name}>
                            {item.name}
                        </span>
                        <span className="font-size text-ellipsis secondary-text" aria-label={value}>
                            {value}
                        </span>
                        <div id={hiddenAriaDivSafeId} className="hidden">{Resources.ListItemRoleDescription}</div>
                    </div>
                    <span className="flex-grow flex-row justify-end">
                        {!item.disableDelete && (
                            <div className="flex-column justify-center margin-right-16">
                                <Button
                                    ariaLabel={message}
                                    className="bolt-list-cell-content-reveal"
                                    iconProps={{ iconName: "Delete" }}
                                    onClick={(event: React.MouseEvent | React.KeyboardEvent) => {
                                        this.props.store.deleteVariable(index);
                                        event.preventDefault();
                                    }}
                                    subtle={true}
                                    tooltipProps={{ text: message }}
                                />
                            </div>
                        )}
                        { this.props.allowEditing && <Icon iconName="ChevronRightMed" size={IconSize.medium} /> }
                    </span>
                </div>
            </ListItem>
        );
    };

    private _createVariable = () => {
        this._renderVariablePanel({} as IVariableData, this.props.store.runtimeVariables.length, true);
    };

    private _updateVariable = (event: React.SyntheticEvent<HTMLElement, Event>, listRow: IListRow<IVariableData>) => {
        this._renderVariablePanel({ ...listRow.data }, listRow.index, false);
    };

    private _renderVariablePanel = (variable: IVariableData, index: number, isNewVariable: boolean) => {
        const layoutManager = this.props.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout((onDismiss: any) => {
            return React.createElement(VariablePanel, {
                isNewVariable: isNewVariable,
                onDismiss: onDismiss,
                onVariableAdded: variable => this.props.store.addVariable(variable),
                onVariableUpdated: variable => this.props.store.updateVariable(variable, index),
                runtimeVariables: this.props.store.runtimeVariables,
                variable: variable
            } as IVariablePanelProps);
        });
    };

    private getFooterButtonProps(): IButtonProps[] {
        const addVariableButtonProps = [
            {
                text: Resources.AddVariableButton,
                primary: true,
                onClick: this._createVariable
            }
        ];

        return this.props.enforceSettableVar ? [] : addVariableButtonProps;
    }
}
